// src/pages/CreateAccountPage.js
import React, { useContext, useState } from "react";
import { LanguageContext } from "../context/LanguageContext";
import { useNavigate } from "react-router-dom";
import { apiRequest } from "../services/api";

function CreateAccountPage() {
  const { lang } = useContext(LanguageContext);
  const navigate = useNavigate();

  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    city: "",
    address: "",
  });

  const TEXT = {
    EN: {
      title: "CREATE ACCOUNT",
      create: "Create Account",
      required: "All fields are required except email.",
      already: "Already have an account?",
      login: "Login",
      support: "Support / Messages",
    },
    AR: {
      title: "إنشاء حساب",
      create: "إنشاء حساب",
      required: "كل الحقول مطلوبة ما عدا البريد الإلكتروني.",
      already: "لديك حساب بالفعل؟",
      login: "تسجيل الدخول",
      support: "الدعم والرسائل",
    },
  };

  const t = TEXT[lang];

  // 📌 AUTO-FORMAT PHONE NUMBER (unchanged logic)
  const formatPhone = (val) => {
    if (val.startsWith("+") && !val.startsWith("+961")) return val;

    let digits = val.replace(/\D/g, "");

    if (val.startsWith("+961")) {
      digits = "961" + digits.slice(3);
    }

    if (digits.startsWith("961")) {
      const rest = digits.slice(3);
      return `+961 ${rest.slice(0, 2)} ${rest.slice(2, 5)} ${rest.slice(5, 8)}`.trim();
    }

    if (/^[078]/.test(digits)) {
      if (digits.length <= 2) return digits;
      if (digits.length <= 5)
        return `${digits.slice(0, 2)} ${digits.slice(2)}`;
      return `${digits.slice(0, 2)} ${digits.slice(2, 5)} ${digits.slice(5, 8)}`;
    }

    return val;
  };

  const handleChange = (k, v) =>
    setForm((prev) => ({ ...prev, [k]: v }));

  const submit = async () => {
    if (!form.name || !form.phone || !form.city || !form.address) {
      alert(
        lang === "EN"
          ? "Please fill all required fields."
          : "يرجى ملء جميع الحقول المطلوبة"
      );
      return;
    }

    try {
      // 1️⃣ Create guest user
      const auth = await apiRequest("/auth/guest", {
        method: "POST",
        body: JSON.stringify({
          full_name: form.name,
          phone: form.phone,
          email: form.email || null,
        }),
      });

      localStorage.setItem("esill-token", auth.token);

      // 2️⃣ Merge guest cart
      const sessionToken = localStorage.getItem("esill-session");
      if (sessionToken) {
        try {
          await apiRequest("/cart/merge", {
            method: "POST",
            body: JSON.stringify({ session_token: sessionToken }),
          });
          localStorage.removeItem("esill-session");
          window.dispatchEvent(new Event("auth-changed"));
        } catch (e) {
          console.warn("Cart merge failed", e);
        }
      }

      // 3️⃣ Save address
      const user = await apiRequest("/users/me", {
        method: "PUT",
        body: JSON.stringify({
          full_name: form.name,
          phone: form.phone,
          email: form.email || null,
          city: form.city,
          area: form.address,
          street: "",
          building: "",
        }),
      });

      localStorage.setItem("esill-user", JSON.stringify(user));

      navigate("/account");
    } catch (err) {
      alert(err.message);
    }
  };

  return (
    <div
      className="w-full min-h-screen bg-[#0F1A12] text-white pt-[95px] px-4 pb-24"
      dir={lang === "AR" ? "rtl" : "ltr"}
    >
      {/* BACK */}
      <button
        onClick={() => navigate(-1)}
        className="absolute top-[80px] text-3xl opacity-70 hover:opacity-100"
        style={lang === "AR" ? { right: 16 } : { left: 16 }}
      >
        ×
      </button>

      {/* TITLE */}
      <h1 className="text-xl font-semibold text-center tracking-wide">
        {t.title}
      </h1>
      <div className="h-[2px] w-20 bg-[#2E5A2A] mx-auto mt-2 mb-4"></div>

      <p className="text-center text-xs opacity-70 mb-6">
        {t.required}
      </p>

      {/* FORM */}
      <div className="max-w-md mx-auto bg-[#121F17] border border-[#2E5A2A] rounded-lg p-5 space-y-4">
        <Field
          placeholder={lang === "EN" ? "Full Name" : "الاسم الكامل"}
          value={form.name}
          onChange={(v) => handleChange("name", v)}
        />

        <Field
          placeholder={lang === "EN" ? "Phone Number" : "رقم الهاتف"}
          value={form.phone}
          onChange={(v) => handleChange("phone", formatPhone(v))}
        />

        <Field
          placeholder={
            lang === "EN"
              ? "Email (optional)"
              : "البريد الإلكتروني (اختياري)"
          }
          value={form.email}
          onChange={(v) => handleChange("email", v)}
        />

        <Field
          placeholder={lang === "EN" ? "City" : "المدينة"}
          value={form.city}
          onChange={(v) => handleChange("city", v)}
        />

        <Field
          placeholder={lang === "EN" ? "Address" : "العنوان"}
          value={form.address}
          onChange={(v) => handleChange("address", v)}
        />

        <button
          onClick={submit}
          className="
            w-full py-3 rounded-md
            bg-[#2E5A2A]
            font-semibold text-lg
            hover:brightness-110
            transition active:scale-[0.98]
          "
        >
          {t.create}
        </button>

        <button
          onClick={() => navigate("/contact")}
          className="
            w-full py-3 rounded-md
            border border-[#2E5A2A]
            text-sm font-semibold
            hover:bg-[#2E5A2A]
            transition
          "
        >
          {t.support}
        </button>

        {/* LOGIN */}
        <div className="text-center pt-2 text-sm opacity-80">
          {t.already}{" "}
          <button
            onClick={() => {
              localStorage.setItem("esill-open-login", "1");
              navigate("/");
            }}
            className="underline font-semibold hover:opacity-100"
          >
            {t.login}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ placeholder, value, onChange }) {
  return (
    <input
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      className="
        w-full px-4 py-3 rounded-md text-sm
        bg-[#0F1A12] text-white
        border border-[#2E5A2A]
        focus:outline-none
      "
    />
  );
}

export default CreateAccountPage;
